import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, ComposedChart,
  ScatterChart, Scatter, ZAxis, ReferenceLine,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import { buildMarketMetrics } from "./Kocampaparse";
import BrandView from "./Brandview";

const T = {
  surface: "#FFFFFF", bgAlt: "#FFF8F8", border: "#FADDDD", borderMed: "#F5BABA",
  accent: "#E8001A", accentDark: "#B50015", accentDeep: "#800010", accentSoft: "#FFF0F0",
  text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060",
  pos: "#1A7A3A", neg: "#C42020", warn: "#B06000", ink: "#12100E",
  shadow: "0 2px 8px rgba(232,0,26,0.08)", shadowMd: "0 4px 16px rgba(232,0,26,0.12)",
};

const BRAND_COLORS = ["#E8001A", "#1A7A9A", "#B06000", "#5A8A2A", "#7A3A8A",
                      "#C42020", "#0A5A7A", "#E8701A", "#3A6A9A", "#8A2E5E"];

/* ── Formatters ──────────────────────────────────────────────────────── */
const fmtVol = (v) => {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toFixed(0);
};
const fmtPct  = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(d)}%`);
const fmtSign = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(d)}%`);
const gColor  = (v) => (v == null ? T.textDim : v > 0.0005 ? T.pos : v < -0.0005 ? T.neg : T.textDim);

const Tip = ({ active, payload, label, pct }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(255,255,255,0.97)", border: `1px solid ${T.borderMed}`,
      borderRadius: 8, padding: "10px 14px", fontSize: 11, boxShadow: T.shadowMd }}>
      {label && <div style={{ color: T.textDim, marginBottom: 6, fontSize: 10,
        textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 18 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color || p.fill }} />
            <span style={{ color: T.textMid }}>{p.name}</span>
          </span>
          <span style={{ color: T.text, fontWeight: 600 }}>
            {pct ? `${Number(p.value).toFixed(1)}%` : fmtVol(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

function Card({ title, subtitle, badge, children, style = {} }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
      padding: 16, display: "flex", flexDirection: "column", boxShadow: T.shadow, ...style }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{title}</div>
          {subtitle && <div style={{ color: T.textDim, fontSize: 10.5, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {badge && <div style={{ background: T.accentSoft, border: `1px solid ${T.borderMed}`,
          color: T.accent, fontSize: 9, padding: "3px 8px", borderRadius: 4, fontWeight: 700 }}>{badge}</div>}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

/* Inline bullet bar — the deck's signature "bar inside a table cell" */
function Bullet({ value, max, color, label, width = 96 }) {
  const pct = max ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
      <div style={{ width, height: 9, background: "#EFEFEF", borderRadius: 3, overflow: "hidden", flexShrink: 0 }}>
        <div style={{ width: `${pct * 100}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ minWidth: 44, textAlign: "right", fontSize: 11, fontWeight: 600, color: T.text }}>{label}</span>
    </div>
  );
}

/* Ratios above ~2x read better as "6X" than "600%" — the deck does this too */
const respLabel = (r) => {
  if (r == null) return "\u2014";
  return r >= 2 ? `${Math.round(r)}X` : `${(r * 100).toFixed(0)}%`;
};

const Pill = ({ v }) => (
  <span style={{
    display: "inline-block", minWidth: 52, textAlign: "center",
    padding: "2px 8px", borderRadius: 10, fontSize: 10.5, fontWeight: 700,
    color: v == null ? T.textDim : v >= 0 ? "#0F5C2A" : "#8E1414",
    background: v == null ? "#F2F2F2" : v >= 0 ? "#D9F0E1" : "#FBDADA",
  }}>{fmtSign(v, 0)}</span>
);

/* ══════════════════════════════════════════════════════════════════════ */
export default function KoCampaView({ data, brand = null, onAskAI }) {
  const { months, brands = [], packs = [] } = data;
  const [month, setMonth]   = useState(months[months.length - 1] || "");
  const [metric, setMetric] = useState("ko");      // scatter x-axis — total KO volume by default
  const prevMonth = months[Math.max(0, months.indexOf(month) - 1)];

  const markets = useMemo(
    () => buildMarketMetrics(data, month, prevMonth).sort((a, b) => b.campa - a.campa),
    [data, month, prevMonth]
  );

  /* All-India totals for the KPI strip */
  const totals = useMemo(() => {
    const sum = (arr, f) => arr.reduce((s, x) => s + (f(x) || 0), 0);
    const koTotal = sum(markets, m => m.koTotal);
    const koAff   = sum(markets, m => m.koAff);
    const campa   = sum(markets, m => m.campa);
    const koAffP  = sum(markets, m => m.koAffPrev);
    const campaP  = sum(markets, m => m.campaPrev);
    return {
      koTotal, koAff, campa,
      affMix:   koTotal ? koAff / koTotal : 0,
      response: campa ? koAff / campa : null,
      koGrowth: koAffP ? koAff / koAffP - 1 : null,
      campaGrowth: campaP ? campa / campaP - 1 : null,
    };
  }, [markets]);

  /* Monthly trend: KO affordable vs Campa, all markets */
  const trend = useMemo(() => months.map(m => {
    const ko = data.koRows.filter(r => r.month === m && r.key !== "allindia");
    const cp = data.campaRows.filter(r => r.month === m && r.key !== "allindia");
    const koAff = ko.filter(r => r.affordable).reduce((s, r) => s + r.vol, 0);
    return {
      month: m,
      koAffordable: koAff,
      koTotal: ko.reduce((s, r) => s + r.vol, 0),
      campa: cp.reduce((s, r) => s + r.vol, 0),
    };
  }).map(d => ({ ...d, response: d.campa ? (d.koAffordable / d.campa) * 100 : 0 })), [data, months]);

  /* Brand and pack mix for the selected month */
  const mix = (field, top) => {
    const acc = {};
    data.koRows.filter(r => r.month === month && r[field]).forEach(r => {
      acc[r[field]] = (acc[r[field]] || 0) + r.vol;
    });
    return Object.entries(acc).map(([name, vol]) => ({ name, vol }))
      .sort((a, b) => b.vol - a.vol).slice(0, top);
  };
  const brandMix = useMemo(() => mix("brand", 8), [data, month]);
  const packMix  = useMemo(() => mix("pack", 8),  [data, month]);

  const maxCampa = Math.max(...markets.map(m => m.campa), 1);
  const maxMix = Math.max(...markets.map(m => m.affMix), 0.05);

  /* The deck splits franchise bottlers from company markets with a rule,
     and outlines the biggest Campa market inside each block. */
  const groupedMarkets = useMemo(() => {
    const FRANCHISE = /slmg|moon|kgb|lbpl|bbpl|dbpl|sssl|kandhari|superior|bengal|diamond|ludhiana|waves|dankuni|taratala|rajmundry/i;
    const mk = (rows) => ({
      rows,
      peakKey: rows.reduce((best, r) => (!best || r.campa > best.campa ? r : best), null)?.key,
    });
    const fr = markets.filter(m => FRANCHISE.test(m.market));
    const co = markets.filter(m => !FRANCHISE.test(m.market));
    return [fr.length && mk(fr), co.length && mk(co)].filter(Boolean);
  }, [markets]);
  const medCampa = markets.length ? [...markets].sort((a, b) => a.campa - b.campa)[Math.floor(markets.length / 2)].campa : 0;
  const respVals = markets.map(m => m.response).filter(v => v != null);
  const medResp  = respVals.length ? [...respVals].sort((a, b) => a - b)[Math.floor(respVals.length / 2)] : 0;

  const scatter = markets.filter(m => m.campa > 0 && m.response != null).map(m => ({
    ...m,
    x: metric === "campa" ? m.campa : m.koTotal,
    y: Math.min(m.response * 100, 150),      // clamp to the axis ceiling
    yTrue: m.response * 100,                 // real value for the tooltip
    z: m.koAff,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 16 }}>
      {/* ── Headline strip, styled after the deck's title bar ── */}
      <div style={{ background: T.ink, borderRadius: 10, padding: "14px 20px", color: "#fff",
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.2 }}>
          KO Affordable {fmtVol(totals.koAff)} UC · Campa {fmtVol(totals.campa)} UC ·
          Response {fmtPct(totals.response, 0)}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 9.5, letterSpacing: 1, opacity: 0.6, textTransform: "uppercase" }}>Month</span>
          <select value={month} onChange={e => setMonth(e.target.value)}
            style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 6, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>
            {months.map(m => <option key={m} value={m} style={{ color: "#000" }}>{m}</option>)}
          </select>
          {onAskAI && <button onClick={onAskAI} style={{ background: T.accent, border: "none", color: "#fff",
            padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>🤖 Ask AI</button>}
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
        {[
          { label: "KO Total Volume",     value: fmtVol(totals.koTotal), color: T.accent,     icon: "🥤" },
          { label: "KO Affordable",       value: fmtVol(totals.koAff),   color: T.accentDark, icon: "💰" },
          { label: "Affordable Mix",      value: fmtPct(totals.affMix),  color: T.pos,        icon: "📊" },
          { label: "Campa Volume",        value: fmtVol(totals.campa),   color: T.neg,        icon: "⚠️" },
          { label: "Competitive Response",value: fmtPct(totals.response, 0), color: T.warn,   icon: "⚔️", sub: "KO affordable ÷ Campa" },
        ].map(k => (
          <div key={k.label} style={{ background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 10, padding: 16, boxShadow: T.shadow, borderTop: `3px solid ${k.color}` }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ color: T.textDim, fontSize: 10, letterSpacing: 1.2,
                textTransform: "uppercase", fontWeight: 600 }}>{k.label}</div>
              <span style={{ fontSize: 18, opacity: 0.25 }}>{k.icon}</span>
            </div>
            <div style={{ color: T.text, fontSize: 22, fontWeight: 700, marginTop: 6 }}>{k.value}</div>
            {k.sub && <div style={{ color: T.textDim, fontSize: 9.5, marginTop: 3 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* ── Market scorecard, laid out like the source deck ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: 16, boxShadow: T.shadow }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
            tableLayout: "fixed", fontSize: 11.5 }}>
            <thead>
              <tr style={{ background: T.ink, color: "#fff" }}>
                <th style={{ ...thD, ...marketCell, textAlign: "left" }}>Market</th>
                <th style={thD}>
                  Campa Vol UC 0-20
                  <div style={subHd}>Projected Vol UC<br />Indexed till date</div>
                </th>
                <th style={thD}>Affordable<br />Total</th>
                <th style={thD}>Affordable<br />Mix w/n SPK</th>
                <th style={thD}>KO Competitive Response<br />as Percentage of Campa</th>
                <th style={thD}>Vol UC Growth<br />of 0-20 SPK</th>
              </tr>
            </thead>
            <tbody>
              {groupedMarkets.map((grp, gi) => grp.rows.map((m, ri) => {
                // The deck outlines the largest Campa market in each block
                const isPeak = m.key === grp.peakKey;
                return (
                  <tr key={m.key} style={{
                    borderTop: (gi && !ri) ? `2px solid ${T.borderMed}` : "none",
                  }}>
                    <td title={m.market}
                      style={{ ...tdD, ...marketCell, textAlign: "left", fontWeight: 700 }}>
                      {m.market}
                    </td>
                    <td style={tdD}>
                      <span style={isPeak ? peakBox : undefined}>{fmtVol(m.campa)}</span>
                    </td>
                    <td style={{ ...tdD, fontWeight: 700 }}>{fmtVol(m.koAff)}</td>
                    <td style={tdD}>
                      <Bullet value={m.affMix} max={maxMix} color={T.pos}
                        label={fmtPct(m.affMix)} width={58} />
                    </td>
                    <td style={tdD}>
                      <Bullet value={Math.min(m.response ?? 0, 1)} max={1} color={T.accent}
                        label={respLabel(m.response)} width={68} />
                    </td>
                    <td style={tdD}><Pill v={m.growth} /></td>
                  </tr>
                );
              }))}

              {/* ALL INDIA — outlined, exactly as the deck closes the table.
                  Built from ordinary cells (not a nested table) so the
                  columns stay aligned with the rows above. */}
              <tr style={{ background: "#FAFAFA" }}>
                <td style={{ ...tdD, ...marketCell, ...allIndiaCell, textAlign: "left",
                  fontWeight: 800, borderLeft: `2px solid ${T.accent}`,
                  borderTopLeftRadius: 6, borderBottomLeftRadius: 6 }}>ALL INDIA</td>
                <td style={{ ...tdD, ...allIndiaCell, fontWeight: 800 }}>{fmtVol(totals.campa)}</td>
                <td style={{ ...tdD, ...allIndiaCell, fontWeight: 800 }}>{fmtVol(totals.koAff)}</td>
                <td style={{ ...tdD, ...allIndiaCell }}>
                  <Bullet value={totals.affMix} max={maxMix} color={T.pos}
                    label={fmtPct(totals.affMix)} width={58} />
                </td>
                <td style={{ ...tdD, ...allIndiaCell }}>
                  <Bullet value={Math.min(totals.response ?? 0, 1)} max={1}
                    color={T.accent} label={respLabel(totals.response)} width={68} />
                </td>
                <td style={{ ...tdD, ...allIndiaCell, borderRight: `2px solid ${T.accent}`,
                  borderTopRightRadius: 6, borderBottomRightRadius: 6 }}>
                  <Pill v={totals.koGrowth} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Legend strip, as printed under the deck's table */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 12,
          paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: 10,
          color: T.textMid, alignItems: "center" }}>
          <span style={lg}><i style={{ ...sw, background: T.pos }} />Affordable Mix w/n SPK</span>
          <span style={lg}><i style={{ ...sw, background: T.accent }} />KO Competitive Response as % of Campa</span>
          <span style={lg}><i style={{ ...sw, background: "#D9F0E1", border: "1px solid #9BD3AF" }} />+ Positive</span>
          <span style={lg}><i style={{ ...sw, background: "#FBDADA", border: "1px solid #E9A8A8" }} />− Negative</span>
          <span style={{ marginLeft: "auto", color: T.accent, fontWeight: 700 }}>
            *KO {fmtVol(totals.koAff)}@10
          </span>
        </div>
      </div>

      {/* ── Threat quadrant + trend ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Card title="Competitive Threat Quadrant" badge="BUBBLE"
          subtitle="Campa scale × KO response · bubble = KO affordable volume">
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {[["campa", "vs Campa volume"], ["ko", "vs KO total volume"]].map(([id, lab]) => (
              <button key={id} onClick={() => setMetric(id)} style={{
                padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${metric === id ? T.accent : T.border}`,
                background: metric === id ? T.accentSoft : "transparent",
                color: metric === id ? T.accent : T.textDim }}>{lab}</button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <ScatterChart margin={{ top: 12, right: 20, bottom: 30, left: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={T.border} />
              <XAxis type="number" dataKey="x" tickFormatter={fmtVol}
                tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false}
                label={{ value: metric === "campa" ? "Campa volume →" : "KO total volume →",
                  position: "insideBottom", offset: -8, fill: T.textDim, fontSize: 10 }} />
              {/* Capped at 150%: a few markets have near-zero Campa volume and
                  produce 400-1000% ratios that flatten everything else. The
                  domain runs slightly past the cap so clamped bubbles sit
                  inside the plot area instead of being clipped by the top edge. */}
              <YAxis type="number" dataKey="y" domain={[0, 162]} allowDataOverflow
                ticks={[0, 25, 50, 75, 100, 125, 150]} tickFormatter={v => `${v}%`}
                tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false}
                label={{ value: "KO response ↑ (capped 150%)", angle: -90, position: "insideLeft",
                  offset: 10, fill: T.textDim, fontSize: 10 }} />
              <ZAxis type="number" dataKey="z" range={[70, 800]} />
              <ReferenceLine x={medCampa} stroke={T.textDim} strokeDasharray="4 4" />
              <ReferenceLine y={medResp * 100} stroke={T.textDim} strokeDasharray="4 4" />
              <Tooltip cursor={{ strokeDasharray: "3 3", stroke: T.accent }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: "rgba(255,255,255,0.97)", border: `1px solid ${T.borderMed}`,
                      borderRadius: 8, padding: "10px 14px", fontSize: 11, boxShadow: T.shadowMd }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.market}</div>
                      <div style={{ color: T.textMid }}>Total KO volume: <b style={{ color: T.text }}>{fmtVol(d.koTotal)}</b></div>
                      <div style={{ color: T.textMid }}>Campa: <b style={{ color: T.text }}>{fmtVol(d.campa)}</b></div>
                      <div style={{ color: T.textMid }}>KO affordable: <b style={{ color: T.text }}>{fmtVol(d.koAff)}</b></div>
                      <div style={{ color: T.textMid }}>Response: <b style={{ color: T.text }}>{fmtPct(d.response, 0)}</b>
                        {d.yTrue > 150 && <span style={{ color: T.textDim, fontSize: 9.5 }}> (plotted at cap)</span>}</div>
                      <div style={{ color: T.textMid }}>Affordable mix: <b style={{ color: T.text }}>{fmtPct(d.affMix)}</b></div>
                    </div>
                  );
                }} />
              <Scatter data={scatter}>
                {scatter.map((d, i) => {
                  const big = d.x >= medCampa, weak = d.y < medResp * 100;
                  const fill = weak ? (big ? T.neg : T.warn) : (big ? T.pos : T.accent);
                  return <Cell key={i} fill={fill} fillOpacity={big && weak ? 0.8 : 0.5} stroke={fill} />;
                })}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6, fontSize: 9.5, color: T.textDim }}>
            {[["Big Campa, weak response", T.neg], ["Small Campa, weak response", T.warn],
              ["Big Campa, strong response", T.pos], ["Small Campa, strong response", T.accent]].map(([l, c]) => (
              <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: c, opacity: 0.7 }} />{l}
              </span>
            ))}
          </div>
        </Card>

        <Card title="KO Affordable vs Campa" badge="TREND"
          subtitle="Monthly volume with response rate overlay">
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={trend} margin={{ top: 16, right: 12, bottom: 20, left: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false} />
              <XAxis dataKey="month" tick={{ fill: T.textDim, fontSize: 10 }}
                axisLine={{ stroke: T.border }} tickLine={false} />
              <YAxis yAxisId="L" tickFormatter={fmtVol} tick={{ fill: T.textDim, fontSize: 10 }}
                axisLine={{ stroke: T.border }} tickLine={false} />
              <YAxis yAxisId="R" orientation="right" tickFormatter={v => `${v.toFixed(0)}%`}
                tick={{ fill: T.warn, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false} />
              <Tooltip content={<Tip />} />
              <Legend wrapperStyle={{ fontSize: 10 }}
                formatter={v => <span style={{ color: T.textMid }}>{v}</span>} />
              <Bar yAxisId="L" dataKey="campa" name="Campa volume" fill={T.neg} fillOpacity={0.75} radius={[3, 3, 0, 0]} />
              <Bar yAxisId="L" dataKey="koAffordable" name="KO affordable" fill={T.accent} fillOpacity={0.9} radius={[3, 3, 0, 0]} />
              <Line yAxisId="R" type="monotone" dataKey="response" name="Response %"
                stroke={T.warn} strokeWidth={2.4} dot={{ r: 3, fill: T.warn, strokeWidth: 0 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ── Affordable mix ranking + brand/pack mix ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 14 }}>
        <Card title="Affordable Mix by Market" badge="RANKED"
          subtitle={`Share of KO volume that is affordable · ${month}`}>
          <ResponsiveContainer width="100%" height={Math.max(260, markets.length * 24 + 40)}>
            <BarChart layout="vertical" data={[...markets].sort((a, b) => b.affMix - a.affMix)}
              margin={{ top: 6, right: 56, bottom: 6, left: 110 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={T.border} horizontal={false} />
              <XAxis type="number" tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false} />
              <YAxis type="category" dataKey="market" tick={{ fill: T.textMid, fontSize: 10 }}
                axisLine={false} tickLine={false} width={110} interval={0} />
              <Tooltip content={<Tip pct />} cursor={{ fill: `${T.accent}08` }} />
              <Bar dataKey="affMix" name="Affordable mix" fill={T.pos} fillOpacity={0.85}
                radius={[3, 3, 3, 3]}>
                <LabelList dataKey="affMix" position="right" formatter={v => `${(v * 100).toFixed(1)}%`}
                  style={{ fill: T.textMid, fontSize: 10 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card title="Brand Mix" subtitle={`Top brands by volume · ${month}`}>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={brandMix} dataKey="vol" nameKey="name" innerRadius="52%" outerRadius="86%"
                  stroke="#fff" strokeWidth={2} paddingAngle={2}>
                  {brandMix.map((_, i) => <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
                <Legend wrapperStyle={{ fontSize: 9.5 }}
                  formatter={v => <span style={{ color: T.textMid }}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Pack Mix" subtitle={`Top containers by volume · ${month}`}>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart layout="vertical" data={packMix} margin={{ top: 4, right: 50, bottom: 4, left: 100 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={T.border} horizontal={false} />
                <XAxis type="number" tickFormatter={fmtVol} tick={{ fill: T.textDim, fontSize: 9.5 }}
                  axisLine={{ stroke: T.border }} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: T.textMid, fontSize: 9.5 }}
                  axisLine={false} tickLine={false} width={100} interval={0} />
                <Tooltip content={<Tip />} cursor={{ fill: `${T.accent}08` }} />
                <Bar dataKey="vol" name="Volume" radius={[3, 3, 3, 3]}>
                  {packMix.map((_, i) => <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} fillOpacity={0.85} />)}
                  <LabelList dataKey="vol" position="right" formatter={fmtVol}
                    style={{ fill: T.textMid, fontSize: 9.5 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>

      {/* ── Brand @10 — VPO and reach by state ── */}
      {brand && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 10, padding: 16, boxShadow: T.shadow }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
            Brand @10 — Zero Sugar vs Campa
          </div>
          <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 2, marginBottom: 12 }}>
            Volume per outlet and weighted distribution by state
          </div>
          <BrandView data={brand} embedded />
        </div>
      )}

      {/* ── Join diagnostics ── */}
      <div style={{ background: T.bgAlt, border: `1px dashed ${T.borderMed}`, borderRadius: 10,
        padding: "10px 14px", fontSize: 10.5, color: T.textDim, lineHeight: 1.6 }}>
        <b style={{ color: T.textMid }}>Market join (BU Ship From → BU Revised):</b>{" "}
        {data.matched.length} BUs matched in both sheets.
        {data.unmappedPlants?.length > 0 && (
          <span style={{ color: T.neg }}>
            {" "}Unmapped KO plants: <b>{data.unmappedPlants.join(", ")}</b> — add a line to
            <code style={{ background: "#fff", padding: "1px 4px", borderRadius: 3, margin: "0 3px" }}>
              KO_PLANT_TO_BU
            </code>in koCampaParse.js to fold them into a BU.
          </span>
        )}
        {data.campaOnly?.length > 0 && (
          <span> Campa-only BUs: {data.campaOnly.map(k => data.label[k] || k).join(", ")}.</span>
        )}
        {data.fuzzyJoins?.length > 0 && (
          <span> Matched after stripping bottler prefix: {data.fuzzyJoins.join(", ")}.</span>
        )}
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: "pointer", color: T.textMid, fontWeight: 600 }}>
            Plant roll-up ({Object.keys(data.plantsByBU || {}).length} BUs)
          </summary>
          <div style={{ marginTop: 6, display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 4 }}>
            {Object.entries(data.plantsByBU || {}).sort().map(([bu, plants]) => (
              <div key={bu} style={{ fontSize: 10 }}>
                <b style={{ color: T.textMid }}>{bu}</b>{" ← "}
                {[...plants].join(" · ")}
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

const thS = { padding: "8px 10px", fontSize: 9.5, textTransform: "uppercase",
  letterSpacing: 0.8, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" };
const tdS = { padding: "6px 10px", borderBottom: `1px solid ${T.border}`,
  textAlign: "right", color: T.text, whiteSpace: "nowrap" };


/* The market column is the only flexible one, so it must be capped — left
   to itself it absorbs every spare pixel and pushes the numbers off screen.
   The full name stays available via the cell's title attribute. */
const marketCell = {
  width: "22%", maxWidth: 190, overflow: "hidden",
  textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const allIndiaCell = {
  borderTop: `2px solid ${T.accent}`,
  borderBottom: `2px solid ${T.accent}`,
};

const thD = { padding: "8px 8px", fontSize: 9.5, textTransform: "uppercase",
  letterSpacing: 0.4, fontWeight: 700, textAlign: "center",
  whiteSpace: "normal", lineHeight: 1.35 };
const tdD = { padding: "4px 8px", borderBottom: `1px solid ${T.border}`,
  textAlign: "center", color: T.text, whiteSpace: "nowrap" };
const subHd = { fontSize: 8, fontWeight: 500, opacity: 0.7,
  textTransform: "none", letterSpacing: 0, marginTop: 2 };
const peakBox = { display: "inline-block", padding: "2px 14px", borderRadius: 5,
  border: "1.5px solid #E8A33D", background: "#FDF6E8", fontWeight: 700 };
const lg = { display: "inline-flex", alignItems: "center", gap: 6 };
const sw = { width: 11, height: 11, borderRadius: 2, display: "inline-block" };